import { StatusCodes } from "http-status-codes";

const ErrorKeys = [
  // generic errors
  "INTERNAL_SERVER_ERROR",
  "BAD_REQUEST",
  "CONFLICT",
  "NOT_FOUND",
  "POST_NOT_FOUND",
  "TOO_MANY_REQUESTS",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "IM_A_TEAPOT",
  "ALREADY_EXISTS",
  "ALREADY_COMPLETED",
  "NOT_IMPLEMENTED",
  "ZOD_ERROR",

  // auth
  "EMAIL_NOT_REGISTERED",
  "USERNAME_EXISTS",
  "EMAIL_EXISTS",
  "CANNOT_CHANGE_EMAIL_OF_GOOGLE_ACCOUNT",
  "INVALID_OLD_PASSWORD",
  "INVALID_FIREBASE_TOKEN",
  "FIREBASE_TOKEN_REQUIRED",

  // posts
  "FEED_AGGREGATION_FAILED",

  // gallery
  "CREATOR_CANNOT_FOLLOW",

  // users
  "USER_NOT_FOUND",
  "TAGGED_USER_NOT_FOUND",
  "CREATOR_NOT_FOUND",
  "CREATOR_CANNOT_BE_COLLABORATOR",
  "USER_ALREADY_FOLLOWING",
  "USER_FOLLOWING_SELF",
  "USER_BLOCKED",

  // comments
  "PARENT_COMMENT_NOT_FOUND",

  // seed
  "DONATION_FAILED",
  "SEED_AMOUNT_TOO_LOW",
  "CANNOT_DONATE_TO_SELF",
  "CANNOT_SEND_SEED_TO_SELF",
  "WALLET_NOT_FOUND",
  "INSUFFICIENT_FUNDS",

  // messaging
  "INVALID_PARTICIPANTS",
  "USER_MESSAGING_DISABLED",

  // query params
  "INVALID_PAGE",
  "INVALID_LIMIT",
  "INVALID_SORT",
  "INVALID_SLUG",
  "INVALID_TIME_WINDOW",

  // validation
  "VALIDATION_ERROR",

  // other
  "DEVICE_NOT_FOUND",
  "CATEGORY_NOT_FOUND",
  "SUBCATEGORY_NOT_FOUND",
] as const;

type ErrorKey = (typeof ErrorKeys)[number];

export interface GardenError {
  httpCode: number;
  gardenCode: number;
  message: string; // server message
  customUserMessage?: string;
}

export const ERRORS: Record<ErrorKey, GardenError> = {
  // just an example
  IM_A_TEAPOT: {
    httpCode: StatusCodes.IM_A_TEAPOT,
    gardenCode: 0,
    message: "Server refuses to brew coffee.",
    customUserMessage: "We do not tolerate coffee drinkers at the moment",
  },

  INTERNAL_SERVER_ERROR: {
    httpCode: StatusCodes.INTERNAL_SERVER_ERROR,
    message: "Internal server error",
    gardenCode: 1,
  },

  BAD_REQUEST: {
    httpCode: StatusCodes.BAD_REQUEST,
    message: "Bad request",
    gardenCode: 2,
  },

  CONFLICT: {
    httpCode: StatusCodes.CONFLICT,
    message: "Conflict",
    gardenCode: 3,
  },

  NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    message: "Not found",
    gardenCode: 4,
  },

  POST_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    message: "Post not found",
    gardenCode: 5,
  },

  USER_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 6,
    message: "User not found",
  },

  TOO_MANY_REQUESTS: {
    httpCode: StatusCodes.TOO_MANY_REQUESTS,
    gardenCode: 7,
    message: "Too many requests",
  },

  UNAUTHORIZED: {
    httpCode: StatusCodes.UNAUTHORIZED,
    gardenCode: 8,
    message: "Unauthorized",
  },

  FORBIDDEN: {
    httpCode: StatusCodes.FORBIDDEN,
    gardenCode: 9,
    message: "Insufficient permissions",
  },

  // Custom errors
  SEED_AMOUNT_TOO_LOW: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 10,
    message: "Seed amount too low",
    customUserMessage: "Seed amount too low",
  },

  DONATION_FAILED: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 11,
    message: "Donation failed",
  },

  TAGGED_USER_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 12,
    message: "Tagged user not found",
    customUserMessage: "Tagged user not found",
  },

  PARENT_COMMENT_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 13,
    message: "Parent comment not found",
  },

  CREATOR_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 14,
    message: "Creator not found",
  },

  DEVICE_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 15,
    message: "Device not found",
  },

  CANNOT_DONATE_TO_SELF: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 16,
    message: "Cannot donate to own post",
  },

  WALLET_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 17,
    message: "Wallet not found",
  },

  FEED_AGGREGATION_FAILED: {
    httpCode: StatusCodes.INTERNAL_SERVER_ERROR,
    gardenCode: 18,
    message: "Feed aggregation failed",
  },

  INVALID_PAGE: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 19,
    message: "Invalid page parameter",
  },

  INVALID_LIMIT: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 20,
    message: "Invalid limit parameter",
  },

  VALIDATION_ERROR: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 21,
    message: "Validation error",
  },

  INVALID_SORT: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 22,
    message: "Invalid sort parameter",
  },

  INVALID_TIME_WINDOW: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 23,
    message: "Invalid time window parameter",
  },

  NOT_IMPLEMENTED: {
    httpCode: StatusCodes.NOT_IMPLEMENTED,
    gardenCode: 24,
    message: "Not implemented",
  },

  CREATOR_CANNOT_BE_COLLABORATOR: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 25,
    message: "Creator cannot be collaborator",
  },

  INVALID_PARTICIPANTS: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 26,
    message: "Invalid number of participants",
  },

  USER_MESSAGING_DISABLED: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 27,
    message: "User messaging disabled",
  },

  ALREADY_EXISTS: {
    httpCode: StatusCodes.CONFLICT,
    gardenCode: 28,
    message: "Already exists",
  },

  CANNOT_SEND_SEED_TO_SELF: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 29,
    message: "Cannot send SEED to self",
  },

  INSUFFICIENT_FUNDS: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 30,
    message: "Insufficient funds",
  },

  INVALID_SLUG: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 31,
    message: "Invalid slug",
  },

  ALREADY_COMPLETED: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 32,
    message: "Already completed",
  },

  CREATOR_CANNOT_FOLLOW: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 33,
    message: "Creator cannot follow own gallery",
  },

  CATEGORY_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 34,
    message: "Category not found",
  },

  SUBCATEGORY_NOT_FOUND: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 35,
    message: "Subcategory not found",
  },

  EMAIL_NOT_REGISTERED: {
    httpCode: StatusCodes.NOT_FOUND,
    gardenCode: 36,
    message: "Email not registered",
  },

  USERNAME_EXISTS: {
    httpCode: StatusCodes.CONFLICT,
    gardenCode: 37,
    message: "Username exists",
  },

  EMAIL_EXISTS: {
    httpCode: StatusCodes.CONFLICT,
    gardenCode: 38,
    message: "Email exists",
  },

  CANNOT_CHANGE_EMAIL_OF_GOOGLE_ACCOUNT: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 39,
    message: "Cannot change email of Google account",
  },

  INVALID_OLD_PASSWORD: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 40,
    message: "Invalid old password",
  },

  INVALID_FIREBASE_TOKEN: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 41,
    message: "Invalid Firebase token",
  },

  USER_ALREADY_FOLLOWING: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 42,
    message: "User already following",
  },
  USER_FOLLOWING_SELF: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 43,
    message: "User cannot follow self",
  },

  USER_BLOCKED: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 43,
    message: "User blocked",
  },

  ZOD_ERROR: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 44,
    message: "Zod validation error",
  },

  FIREBASE_TOKEN_REQUIRED: {
    httpCode: StatusCodes.BAD_REQUEST,
    gardenCode: 45,
    message: "Firebase token required",
  },
};

export const parseErrorCode = (gardenCode: number): ErrorKey | undefined => {
  return Object.keys(ERRORS).find(
    (key) => ERRORS[key as ErrorKey].gardenCode === gardenCode
  ) as ErrorKey;
};
